// src/views/Warehouse.jsx
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { FaSlidersH, FaDove, FaShoePrints } from 'react-icons/fa';
import Split from 'react-split';
import './Warehouse.css';
import { showToast, hideToast, updateToast } from '../utils/toast';
import Warehouse3D from './Warehouse3D';

// Walk icon uses react-icons FaShoePrints

// Bird icon for Observer Walk uses react-icons FaDove

const API = import.meta.env.VITE_API_URL;
// Reuse the same LS keys used by Designer so selections sync across views
const WH_PICK_LS   = 'wd_selected_warehouse_v1';
const AREA_PICK_LS = 'wd_selected_area_v2';

const pad3 = n => (n == null || n === '' ? '' : String(n).padStart(3, '0'));
const pad2 = n => (n == null || n === '' ? '' : String(n).padStart(2, '0'));

const COLS_LS_KEY       = 'wh_table_col_widths_v1';
const RANGE_LS_KEY      = 'wh_range_v1';              // persist the range inputs
const INNER_SPLIT_LS_KEY = 'wh_inner_split_v1';       // persist left(list)/right(3D) split
const SHOW3D_LS_KEY     = 'wh_show_3d_v1';            // persist Show 3D toggle

// ---- Default sort helpers: Aisle -> Bay -> Shelve -> Slot (numeric ASC) ----
const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};
const cmp = (a, b) => (a === b ? 0 : a < b ? -1 : 1);
const defaultSort = (arr) =>
  [...(Array.isArray(arr) ? arr : [])].sort((a, b) =>
    cmp(toNum(a.roww),    toNum(b.roww))    ||
    cmp(toNum(a.columnn), toNum(b.columnn)) ||
    cmp(toNum(a.shelve),  toNum(b.shelve))  ||
    cmp(toNum(a.bin),     toNum(b.bin))
  );

// helper to load persisted range safely
const loadRangeFromLS = () => {
  try {
    const s = localStorage.getItem(RANGE_LS_KEY);
    if (!s) return null;
    const o = JSON.parse(s);
    return {
      aisleFrom: o?.aisleFrom ?? 1,
      aisleTo:   o?.aisleTo   ?? 10,
      bayFrom:   o?.bayFrom   ?? 1,
      bayTo:     o?.bayTo     ?? 10,
    };
  } catch {
    return null;
  }
};

// selection helpers
const getRowId = (r) =>
  r.locationid ?? `${r.roww}|${r.columnn}|${r.shelve}|${r.bin}`;

export default function Warehouse() {
  // Filters / controls
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(() => {
    try { return String(localStorage.getItem(WH_PICK_LS) || ''); } catch { return ''; }
  });
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState(() => {
    try {
      const v = localStorage.getItem(AREA_PICK_LS);
      return v != null ? String(v) : '1';
    } catch { return '1'; }
  });
  const [show3D, setShow3D] = useState(() => {
    try {
      const v = localStorage.getItem(SHOW3D_LS_KEY);
      return v == null ? true : (v === '1' || v === 'true');
    } catch {
      return true;
    }
  });

  // initialize range inputs from localStorage (fallbacks provided)
  const persistedRange = loadRangeFromLS() || {};
  const [aisleFrom, setAisleFrom] = useState(persistedRange.aisleFrom ?? 1);
  const [aisleTo,   setAisleTo]   = useState(persistedRange.aisleTo   ?? 10);
  const [bayFrom,   setBayFrom]   = useState(persistedRange.bayFrom   ?? 1);
  const [bayTo,     setBayTo]     = useState(persistedRange.bayTo     ?? 10);

  const [whDims, setWhDims] = useState({ lengthFt: 2000, widthFt: 1200, heightFt: 32 });
  // track inner split sizes so top controls can match left-pane width (persisted)
  const [innerSizes, setInnerSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(INNER_SPLIT_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => Number.isFinite(n) && n > 0)) {
        const sum = parsed[0] + parsed[1];
        if (Math.abs(sum - 100) > 0.1) {
          const f = 100 / sum;
          return [parsed[0] * f, parsed[1] * f];
        }
        return parsed;
      }
    } catch {}
    return [65, 35];
  });

  // Persist range inputs whenever they change
  useEffect(() => {
    const payload = { aisleFrom, aisleTo, bayFrom, bayTo };
    try { localStorage.setItem(RANGE_LS_KEY, JSON.stringify(payload)); } catch {}
  }, [aisleFrom, aisleTo, bayFrom, bayTo]);

  // Persist inner split sizes whenever they change
  useEffect(() => {
    try { localStorage.setItem(INNER_SPLIT_LS_KEY, JSON.stringify(innerSizes)); } catch {}
  }, [innerSizes]);

  // Persist Show 3D toggle
  useEffect(() => {
    try { localStorage.setItem(SHOW3D_LS_KEY, show3D ? '1' : '0'); } catch {}
  }, [show3D]);

  // Data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.warehouse.locations';
    if (loading && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading locations…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!loading && prevLoadingRef.current) {
      const count = Array.isArray(rows) ? rows.length : 0;
      const formatted = Number(count).toLocaleString('en-US');
      updateToast({ id, message: `Loaded ${formatted} locations.` });
      const t = setTimeout(() => hideToast(id), 2500);
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = loading;
  }, [loading, rows]);
  const [loadProgress, setLoadProgress] = useState(0);

  // Row selection (for highlight) + 3D ref (to focus camera)
  const [selectedKey, setSelectedKey] = useState('');
  const selectedRowElRef = useRef(null); // direct DOM highlight when 3D is hidden
  const w3dRef = useRef(null);

  // ---------- NEW: checkboxes selection state ----------
  const [checkedSet, setCheckedSet] = useState(() => new Set()); // none checked initially
  const allRowIds = useMemo(() => rows.map(getRowId), [rows]);
  const allChecked = rows.length > 0 && checkedSet.size === rows.length;
  const someChecked = checkedSet.size > 0 && !allChecked;
  const headerCheckRef = useRef(null);

  // reflect indeterminate state in native checkbox
  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = someChecked;
    }
  }, [someChecked, allChecked]);

  // Animate an indeterminate progress bar while locations are loading
  useEffect(() => {
    if (!loading) {
      if (loadProgress > 0 && loadProgress < 100) setLoadProgress(100);
      const t = setTimeout(() => setLoadProgress(0), 250);
      return () => clearTimeout(t);
    }
    setLoadProgress(p => (p > 0 ? p : 20));
    const id = setInterval(() => {
      setLoadProgress(prev => {
        if (!loading) return prev;
        const next = prev + Math.random() * 12;
        return Math.min(90, next);
      });
    }, 180);
    return () => clearInterval(id);
  }, [loading]);


  // whenever warehouseId changes, fetch dims
  useEffect(() => {
    if (!warehouseId) return;
    (async () => {
      try {
        const row = await fetch(`${API}/api/warehouses/${encodeURIComponent(warehouseId)}`).then(r => r.json());

        setWhDims({
          lengthFt: Number(row.length) || 2000,
          widthFt:  Number(row.width)  || 1200,
          heightFt: Number(row.height) || 32
        });
      } catch {
        setWhDims({ lengthFt: 200, widthFt: 120, heightFt: 32 });
      }
    })();
  }, [warehouseId]);

  // clear selection whenever rows change (keeps "initially none checked")
  useEffect(() => {
    setCheckedSet(new Set());
  }, [rows]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const count = Array.isArray(rows) ? rows.length : 0;
    const subtitle = count > 0 ? `(${count.toLocaleString()} locations)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Warehouse', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [warehouseId, rows]);

  const toggleAll = (checked) => {
    if (checked) {
      setCheckedSet(new Set(allRowIds));
    } else {
      setCheckedSet(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setCheckedSet(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  // -----------------------------------------------------

  // ----- Table columns (resizable + persisted widths) -----
  const COLUMNS = useMemo(
    () => [
      // Renamed and repurposed: selection column
      { key: 'select',   title: 'All',      defaultWidth: 36,  align: 'center' },
      { key: 'action',   title: 'Actions',  defaultWidth: 96,  align: 'center' },
      { key: 'pallet',   title: 'Pallet Info', defaultWidth: 300 },
      { key: 'type',     title: 'Type',        defaultWidth: 72 },
      { key: 'aisle',    title: 'Aisle',       defaultWidth: 72 },
      { key: 'bay',      title: 'Bay',         defaultWidth: 72 },
      { key: 'shelve',   title: 'Shelve',      defaultWidth: 64 },
      { key: 'slot',     title: 'Slot',        defaultWidth: 64 },
      { key: 'stack',    title: 'Stack',       defaultWidth: 72 },
    ],
    []
  );

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_LS_KEY) || '{}') || {};
      return saved;
    } catch {
      return {};
    }
  });

  // Per-column minimum width based on header text measurement
  const headerRefs = useRef({});
  const [minColWidths, setMinColWidths] = useState({});

  // Columns visible in the table (hide Actions when 3D is hidden)
  const visibleColumns = useMemo(() => (
    show3D ? COLUMNS : COLUMNS.filter(c => c.key !== 'action')
  ), [show3D, COLUMNS]);

  const setHeaderRef = (key) => (el) => {
    headerRefs.current[key] = el || undefined;
  };

  useLayoutEffect(() => {
    const PAD_LR = 20;  // match .th-inner padding + buffer
    const HANDLE_W = 8; // resizer handle width
    const next = {};
    for (const col of visibleColumns) {
      const container = headerRefs.current[col.key];
      if (!container) continue;
      let width;
      if (col.key === 'select') {
        // No text label; allow a smaller minimum to keep checkbox usable
        width = 28;
      } else {
        const label = container.querySelector('.th-label') || container;
        const rect = label.getBoundingClientRect();
        width = Math.ceil(rect.width) + PAD_LR + HANDLE_W;
      }
      next[col.key] = Math.max(28, width);
    }
    setMinColWidths(next);
  }, [visibleColumns]);

  useEffect(() => {
    const onResize = () => {
      const PAD_LR = 20;
      const HANDLE_W = 8;
      const next = {};
      for (const col of visibleColumns) {
        const container = headerRefs.current[col.key];
        if (!container) continue;
        let width;
        if (col.key === 'select') {
          width = 28;
        } else {
          const label = container.querySelector('.th-label') || container;
          const rect = label.getBoundingClientRect();
          width = Math.ceil(rect.width) + PAD_LR + HANDLE_W;
        }
        next[col.key] = Math.max(28, width);
      }
      setMinColWidths(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visibleColumns]);

  const persistWidths = (next) => {
    setColWidths(next);
    try { localStorage.setItem(COLS_LS_KEY, JSON.stringify(next)); } catch {}
  };

  const startResize = (colKey, ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    // Measure from the actual column header width to mirror Receipts behavior
    const th = ev.currentTarget?.closest ? ev.currentTarget.closest('th') : null;
    const measuredW = th?.offsetWidth;
    const colDef = COLUMNS.find(c => c.key === colKey);
    const startW = Number.isFinite(measuredW) && measuredW > 0
      ? measuredW
      : (colWidths[colKey] ?? colDef?.defaultWidth ?? 120);

    // Visual/UX hint: show resize cursor everywhere while dragging
    try { document.body.classList.add('col-resizing'); } catch {}

    const onMove = (e) => {
      const delta = e.clientX - startX;
      // Allow resizing down to a small global minimum; no header-based min
      const newW = Math.max(10, Math.min(800, startW + delta));
      persistWidths({ ...colWidths, [colKey]: newW });
    };
    const onUp = () => {
      try { document.body.classList.remove('col-resizing'); } catch {}
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const widthFor = (key) => {
    if (key === 'select') return '36px'; // keep checkbox column minimal and fixed
    const colDef = COLUMNS.find(c => c.key === key);
    return (colWidths[key] ?? colDef?.defaultWidth ?? 120) + 'px';
  };

  // Applied width for any column (fixed table layout):
  const appliedWidth = (key) => widthFor(key);

  const minWidthFor = (key) => {
    const v = minColWidths[key];
    return ((v != null ? Math.max(10, v) : 10)) + 'px';
  };

  // --- fetch helper ---
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  // --- Warehouses + default ---
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchJSON(`${API}/api/warehouses`);
        const opts = Array.isArray(list) ? list.map(w => ({ id: w.id, name: w.name })) : [];
        setWarehouses(opts);
        if (!warehouseId && opts.length) {
          // fall back to first if nothing persisted
          setWarehouseId(String(opts[0].id));
        }
      } catch {
        setWarehouses([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist warehouse selection
  useEffect(() => {
    if (warehouseId) {
      try { localStorage.setItem(WH_PICK_LS, String(warehouseId)); } catch {}
    }
  }, [warehouseId]);

  // --- Areas for selected warehouse ---
  useEffect(() => {
    if (!warehouseId) return;
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/warehouseareas/warehouseareas?warehouseid=${warehouseId}`);
        const arr = Array.isArray(data) ? data : [];
        setAreas(arr);
        // Prefer persisted area if valid for this warehouse, else default to first
        const areaLS = (() => { try { return localStorage.getItem(AREA_PICK_LS); } catch { return null; } })();
        const lsValid = areaLS && arr.some(a => String(a.id) === String(areaLS));
        if (!arr.length) {
          setAreaId('1');
        } else if (!areaId || !arr.some(a => String(a.id) === String(areaId))) {
          setAreaId(String(lsValid ? areaLS : arr[0].id));
        }
      } catch {
        setAreas([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  // Persist area selection
  useEffect(() => {
    try { localStorage.setItem(AREA_PICK_LS, String(areaId ?? '')); } catch {}
  }, [areaId]);

  // --- Load range (explicit via Refresh / Apply) ---
  const loadRange = async () => {
    if (!warehouseId || !areaId) return;

    // sanitize numbers
    const aFrom = Math.max(1, Number(aisleFrom) || 1);
    const aTo   = Math.max(aFrom, Number(aisleTo) || aFrom);
    const bFrom = Math.max(1, Number(bayFrom) || 1);
    const bTo   = Math.max(bFrom, Number(bayTo) || bFrom);

    setLoading(true);
    try {
      const qs = new URLSearchParams();

      // required
      qs.set('warehouseid', String(warehouseId));
      qs.set('areaid', String(areaId));

      // canonical params
      qs.set('aisleStart', String(aFrom));
      qs.set('aisleEnd',   String(aTo));
      qs.set('bayStart',   String(bFrom));
      qs.set('bayEnd',     String(bTo));

      // compatibility aliases
      qs.set('row_from', String(aFrom));
      qs.set('row_to',   String(aTo));
      qs.set('aisle_from', String(aFrom));
      qs.set('aisle_to',   String(aTo));
      qs.set('fromrow',  String(aFrom));
      qs.set('torow',    String(aTo));
      qs.set('rowstart', String(aFrom));
      qs.set('rowend',   String(aTo));
      qs.set('startrow', String(aFrom));
      qs.set('endrow',   String(aTo));

      qs.set('bay_from', String(bFrom));
      qs.set('bay_to',   String(bTo));
      qs.set('column_from', String(bFrom));
      qs.set('column_to',   String(bTo));
      qs.set('frombay',  String(bFrom));
      qs.set('tobay',    String(bTo));
      qs.set('columnstart', String(bFrom));
      qs.set('columnend',   String(bTo));
      qs.set('startbay', String(bFrom));
      qs.set('endbay',   String(bTo));

      const data = await fetchJSON(`${API}/api/locations/range-with-pallets?${qs.toString()}`);
      // 👉 Enforce default sort regardless of server order
      setRows(defaultSort(Array.isArray(data) ? data : []));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load (warehouse/area change)
  useEffect(() => {
    if (warehouseId) loadRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, areaId]);

  // ---- Row click => focus 3D camera ----
  const onRowClick = (r, ev) => {
    const aisle = Number(r.roww);
    const bay   = Number(r.columnn);
    const shelf = Number(r.shelve) || 1;
    const bin = Number(r.bin);

    // Do not zoom when 3D view is hidden
    if (!show3D) {
      // Avoid full table re-render; toggle class directly for snappy UI
      try {
        const tr = ev?.currentTarget?.closest('tr');
        if (selectedRowElRef.current && selectedRowElRef.current !== tr) {
          selectedRowElRef.current.classList.remove('selected');
        }
        if (tr) {
          tr.classList.add('selected');
          selectedRowElRef.current = tr;
        }
      } catch {}
      return;
    }

    if (Number.isFinite(aisle) && Number.isFinite(bay)) {
      w3dRef.current?.focusLocation({ aisle, bay, shelf, bin });
      setSelectedKey(`${aisle}|${bay}|${shelf}|${r.bin ?? ''}`);
    }
  };

  // ---- Render helpers ----
  const renderCell = (r, key) => {
    switch (key) {
      case 'select': {
        const id = getRowId(r);
        const checked = checkedSet.has(id);
        return (
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => toggleOne(id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
      case 'pallet': {
        // supports either "WR123 · Pallet #5 · Client | WR..." or multi-line from backend
        const raw = (r.palletInfo ?? r.palletinfo ?? '').toString();
        if (!raw.trim()) return '';
        const text = raw.replace(/\s*\|\s*/g, '\n'); // " | " -> newline
        return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
      }
      case 'action': {
        if (!show3D) return null; // Hide walk/fly actions when 3D view is hidden
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <button
              className="btn"
              aria-label="Walk to this location"
              title="Walk to this location"
              onClick={(e) => {
                e.stopPropagation();
                const a = Number(r.roww), b = Number(r.columnn);
                const shelf = Number(r.shelve) || 1;
                const bin = Number(r.bin) || null;
                w3dRef.current?.walkToLocation({ aisle: a, bay: b, shelf, bin });
              }}
            >
              <span className="btn-icon" style={{ marginRight: 0 }} aria-hidden="true"><FaShoePrints size={16} /></span>
            </button>
            <button
              className="btn"
              aria-label="Observer view walk"
              title="Observer view walk"
              onClick={(e) => {
                e.stopPropagation();
                const a = Number(r.roww), b = Number(r.columnn);
                const shelf = Number(r.shelve) || 1;
                const bin = Number(r.bin) || null;
                w3dRef.current?.setObserverMode(true);
                w3dRef.current?.walkToLocation({ aisle: a, bay: b, shelf, bin });
              }}
            >
              <span className="btn-icon" style={{ marginRight: 0 }} aria-hidden="true"><FaDove size={16} /></span>
            </button>
          </div>
        );
      }
      case 'type':
        return (String(r.type) === '2') ? 'RACK' : 'FLOOR';
      case 'aisle':
        return pad3(r.roww);
      case 'bay':
        return pad3(r.columnn);
      case 'shelve':
        return pad2(r.shelve);
      case 'slot':
        return pad2(r.bin);
      case 'stack':
        // Show comma-separated distinct stacks (server populates r.stacks; fallback to r.stack)
        return (r.stacks && String(r.stacks).trim()) ? String(r.stacks) : (r.stack ?? '');
      case 'legacy':
        return `${r.roww ?? ''}-${r.columnn ?? ''}${r.shelve != null ? '-' + r.shelve : ''}${r.bin != null ? '-' + r.bin : ''}`;
      default:
        return r[key] ?? '';
    }
  };

  // ---- Controls markup ----
  const Controls = (
    <div className="controls-wrap">
      <div className="controls-grid" style={{ width: (show3D ? innerSizes[0] : 100) + '%', maxWidth: '100%' }}>
        <fieldset className="wh-fieldset section-warehouse">
          <legend>Which Warehouse</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Select warehouse to view</label>
              <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="wh-field">
              <label>Select area</label>
              <select value={areaId} onChange={e => setAreaId(e.target.value)}>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset section-aisle">
          <legend>Aisle Range</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Starting Aisle</label>
              <input
                type="number"
                min={1}
                value={aisleFrom}
                onChange={e => setAisleFrom(e.target.value)}
              />
            </div>
            <div className="wh-field">
              <label>Ending Aisle</label>
              <input
                type="number"
                min={aisleFrom || 1}
                value={aisleTo}
                onChange={e => setAisleTo(e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset section-bay">
          <legend>Bay Range</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Starting Bay</label>
              <input
                type="number"
                min={1}
                value={bayFrom}
                onChange={e => setBayFrom(e.target.value)}
              />
            </div>
            <div className="wh-field">
              <label>Ending Bay</label>
              <input
                type="number"
                min={bayFrom || 1}
                value={bayTo}
                onChange={e => setBayTo(e.target.value)}
              />
            </div>
          </div>
        </fieldset>
        <div className="controls-apply section-apply">
          <button className="btn primary" onClick={loadRange}>
            <FaSlidersH className="btn-icon" />
            Apply Range
          </button>
        </div>
      </div>
    </div>
  );

  // ---- Table markup (with sticky header & resizers) ----
  const Table = (
    <div className="table-shell fill">
      <table className="wh-table">
        <colgroup>
          {visibleColumns.map((c, idx) => {
            const isLast = idx === visibleColumns.length - 1;
            // Leave last column flexible (auto) so it soaks up slack
            return (
              <col key={c.key} style={isLast ? undefined : { width: widthFor(c.key) }} />
            );
          })}
        </colgroup>
        <thead>
          <tr>
            {visibleColumns.map(col => (
              <th
                key={col.key}
                style={undefined}
              >
                {col.key === 'select' ? (
                  <div
                    className="th-inner"
                    ref={setHeaderRef(col.key)}
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 4px' // minimal padding so 36px fits the checkbox comfortably
                    }}
                  >
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allChecked}
                      onChange={(e) => toggleAll(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      title="Select / deselect all rows"
                    />
                  </div>
                ) : (
                  <div ref={setHeaderRef(col.key)} className="th-inner" style={{ textAlign: col.align || 'left' }}>
                    <span className="th-label">{col.title}</span>
                    {/* Drag handle */}
                    <span
                      className="col-resizer"
                      onMouseDown={(e) => startResize(col.key, e)}
                      title="Drag to resize"
                    />
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={visibleColumns.length}>Loading…</td></tr>
          ) : rows.length ? (
            rows.map(r => {
              const rowKey = `${r.roww}|${r.columnn}|${r.shelve}|${r.bin}`;
              const id = getRowId(r);
              const isChecked = checkedSet.has(id);
              return (
                <tr
                  key={r.locationid}
                  className={`clickable ${selectedKey === rowKey ? 'selected' : ''}`}
                  onClick={(e) => onRowClick(r, e)}
                  title={show3D ? "Go to this location in 3D" : "Select row"}
                >
                  {visibleColumns.map(c => (
                    <td
                      key={c.key}
                      className={c.key === 'pallet' ? 'pallet-lines' : ''}
                      style={(() => {
                        const base = {
                          textAlign: c.align || (c.key === 'select' ? 'center' : 'left'),
                          verticalAlign: c.key === 'select' ? 'middle' : (c.key === 'in_use' ? 'middle' : 'top'),
                        };
                        if (c.key === 'select') {
                          base.padding = '0 4px';
                        }
                        // Widths are controlled via <colgroup>; avoid overriding per-cell widths
                        return base;
                      })()}
                      onClick={(c.key === 'select' || c.key === 'action') ? (e) => e.stopPropagation() : undefined}
                    >
                      {c.key === 'select' ? (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => toggleOne(id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        renderCell(r, c.key)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr><td colSpan={visibleColumns.length}>No locations found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="warehouse-wrap">
      {/* OUTER split: top controls / bottom content */}
      <Split
        className="warehouse-outer-split"
        direction="vertical"
        sizes={[24, 76]}
        minSize={[120, 260]}
        gutterSize={8}
      >
        {/* TOP: controls */}
        <div className="controls-pane">
          {Controls}
          <div className="controls-view-toggle" style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={show3D}
                onChange={(e) => setShow3D(e.target.checked)}
              />
              Show 3D view
            </label>
          </div>
        </div>

        {/* BOTTOM: LEFT table | RIGHT 3D (keep Split mounted; collapse 3D when hidden) */}
        <Split
          className={`warehouse-inner-split ${show3D ? '' : 'hide-3d'}`}
          direction="horizontal"
          sizes={show3D ? innerSizes : [100, 0]}
          onDrag={(s) => setInnerSizes(s)}
          onDragEnd={(s) => setInnerSizes(s)}
          minSize={[320, show3D ? 360 : 0]}
          gutterSize={show3D ? 8 : 0}
        >
          <div className="locations-table-pane">
            {loadProgress > 0 && (
              <div style={{ position: 'sticky', top: 0, zIndex: 7, background: 'transparent', padding: '6px 0 8px 0' }}>
                <div style={{ height: 6, width: `${loadProgress}%`, background: '#21bb50', borderRadius: 4, boxShadow: '0 0 6px rgba(33,187,80,0.35)', transition: 'width 180ms ease' }} />
              </div>
            )}
            {Table}
          </div>
          <div className="three-pane fill">
            {/* Mount 3D only when visible to avoid unnecessary render loop */}
            {show3D ? (
              <Warehouse3D
                ref={w3dRef}
                rows={rows}
                warehouseId={warehouseId}
                areaId={areaId}
                dims={whDims}
                origin="lower-left"   // <-- Aisle 001 along west wall
              />
            ) : null}
          </div>
        </Split>
      </Split>
    </div>
  );
}
